import { createTheme } from '@mui/material/styles';

// Core theme properties
export const baseTheme = {
    palette: {
        background: {
            default: "#eaf2fc",
            paper: "#ffffff",
        },
        primary: {
            main: "#01a3ff",
            light: "#e4f3ff",
            dark: "#0b86cc",
            contrastText: "#fff",
        },
        secondary: {
            main: "#7a7c98",
            light: "#dfe4ff",
            dark: "#565870",
            contrastText: "#fff",
        },
        accent:{
            main: "#8e24aa", // A vibrant purple
            light: "#fbdfff",
            dark: "#5c007a",
            contrastText: "#fff",
        },
        info: {
            main: "#3da7e3",
            light: "#00b2e3",
            dark: "#2471a0",
            contrastText: "#fff",
        },
        success: {
            main: "#9c0",
            light: "#f1f7f0",
            dark: "#667f07",
            contrastText: "#fff",
        },
        error: {
            main: "#f00",
            light: "#ffefef",
            dark: "#aa0000",
            contrastText: "#fff",
        },
        warning: {
            main: "#ff9800",
            light: "#fff5e0",
            dark: "#cc7e00",
            contrastText: "#fff",
        },
        action:{
            "activeBackground": "rgba(0, 0, 0, 0.03)",
            "active": "rgba(0, 0, 0, 0.54)",
            "hover": "rgba(0, 0, 0, 0.04)",
            "hoverOpacity": 0.04,
            "selected": "#eafdda",
            "selectedHover": "#c0f990",
            "selectedOpacity": 0.08,
            "disabled": "rgba(0, 0, 0, 0.26)",
            "disabledBackground": "rgba(0, 0, 0, 0.12)",
            "disabledOpacity": 0.38,
            "focus": "rgba(0, 0, 0, 0.12)",
            "focusOpacity": 0.12,
            "activatedOpacity": 0.12
        }
    },
    text: {
        light: '#ffffff',
        dark: '#363537',
    },
    link: {
        active: '#00517c',
        hover: '#00324d',
    },
    typography: {
        fontFamily: 'Raleway, Arial',
        fontWeightBold: 700,
        fontWeightMedium: 500,
        fontWeightRegular: 400,
        fontSize: 16,
        h1: {
            fontSize: '1.8rem',
            fontFamily: 'Open Sans, Arial',
        },
        h2: {
            fontSize: '1.5rem',
            fontFamily: 'Open Sans, Arial',
        },
        h3: {
            fontSize: '1.3rem',
            lineHeight:'1.7',
            fontFamily: 'Open Sans, Arial',
        },
        h4: {
            fontSize: '1.2rem',
            fontFamily: 'Open Sans, Arial',
        },
        h5: {
            fontSize: '1.1rem',
            fontFamily: 'Open Sans, Arial',
        },
        h6: {
            fontSize: '1rem',
            fontFamily: 'Open Sans, Arial',
        },
        body1: { fontSize: '1rem' },
        body2: { fontSize: '0.875rem' },
    },
    container:{
        bgColor: '#ffffff',
        Shadow: '0 0 5px 0 rgba(0,0,0,.1)',
    },
    body: '#efeeee',
    verticalNavbar: {
        bgColorLight: '#2f323a',
        bgColorDark: '#25282e',
        color: '#5c9df5',
    },
    authBackground : 'radial-gradient(at 50% 30%, #D7E1EC 10%, #FFFFFF 100%)',
};

const authLightTheme = {
    ...baseTheme,
    palette: {
        ...baseTheme.palette,
        info: {
            main: "#2171a6",
            light: "#3f9ad5",
            dark: "#0c4469",
            contrastText: "#fff",
        },
        primary: {
            main: "#2b5b8a",
            light: "#2b5b8a",
            dark: "#2b5b8a",
            contrastText: "#fff",
        },
        success: {
            main: "#639645",
            light: "#88bd66",
            dark: "#365922",
            contrastText: "#fff",
        },
        error: {
            main: "#a90c0c",
            light: "#ff6659",
            dark: "#aa0000",
            contrastText: "#fff",
        },
        warning: {
            main: "#ff9800",
            light: "#ffbf33",
            dark: "#cc7e00",
            contrastText: "#fff",
        },
        secondary: {
            main: "#757781",
            light: "#cdcdce",
            dark: "#262834",
            contrastText: "#fff",
        },
        type: 'light',
        background: {
            default: "#efeeee",
            paper: "#ffffff",
        },
    },
    appBar: {
        backgroundColor: '#3F4A56',
        color: '#2f3239',
        borderBottom: '1px solid #ddd',
        zIndex: 1201,
    },
    auth:{
        loginBgColor: '#005b9d',
        registerBgColor: '#3F4A56',
        forgotBgColor: '#3F4A56',
    },
    text: {
        light: '#ffffff',
        dark: '#363537',
        error: '#ff0000',
        warning: '#e56305',
        success: '#8eb417',
    },
    link: {
        active: '#00517c',
        hover: '#00324d',
    },
    components: {
        MuiPickersPopper:{
            styleOverrides: {
                root: {
                    zIndex: '9999!important',
                }
            }
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    // Some CSS
                    backgroundColor: '#01a3ff',
                    color: '#ffffff',
                },
            },
        },
        MuiBreadcrumbs:{
            styleOverrides: {
                root: {
                    color: '#ffffff',
                },
            },
        },
        MuiFormHelperText:{
            styleOverrides: {
                root: {
                    color: '#ff0000',
                },
            },
        },
        MuiCard:{
            styleOverrides: {
                root: {
                    color: '#333333',
                    "& .MuiCardHeader-root":{
                        borderBottom:'1px solid #ddd'
                    },
                    '& .MuiCardContent-root, & .MuiCardContent-root:last-child':{
                        paddingBottom: 0,
                    },
                    "& .MuiCardActions-root":{
                        borderTop: '1px solid #eff2f5',
                        background: 'transparent',
                        '& a': {
                            textDecoration: 'none'
                        }
                    }
                },
            },
        },
        MuiButton:{
            variants: [
                {
                    props: { variant: 'accountButton' },
                    style: ({ theme }) => ({
                        border:'1px solid',
                        textTransform: 'none',
                        borderColor: theme.palette.secondary.dark,
                        color: theme.palette.primary.contrastText,
                        '&:hover': {
                            borderColor: theme.palette.secondary.light,
                            color: theme.palette.secondary.contrastText,
                            '& .MuiTypography-root':{
                                color: theme.palette.secondary.contrastText,
                            }
                        },
                        '& .MuiTypography-root':{
                            color: theme.palette.secondary.contrastText,
                        }
                    }),
                },
            ],
        },
        MuiTablePagination:{
            styleOverrides: {
                root: {
                    '& p': {
                        marginBottom: '0!important',
                    }
                }
            }
        },
        MuiDrawer:{
            styleOverrides: {
                root: {
                    '& .MuiDrawer-paper':{
                        position:'relative'
                    },
                    '& .MuiCardContent-root':{
                        padding:'1rem',
                    }
                }
            }
        },
        MuiMenu:{
            styleOverrides: {
                root:{
                    '& .MuiPaper-root':{
                        borderRadius: '4px',
                        boxShadow: '0 0 10px 0 rgb(0 0 0 / 10%)',
                    },
                    '& .MuiList-root':{
                        padding: '0',
                        '& a':{
                            color: '#000',
                            '& li':{
                                padding: '10px 20px',
                                '&:hover':{
                                    backgroundColor: '#f5f5f5',
                                }
                            }
                        },
                        '& svg':{
                            marginRight: '10px',
                            fontSize:'1rem'
                        },
                    },
                }
            }
        },
        MuiDataGrid:{
            styleOverrides: {
                root: {
                    '& .MuiDataGrid-columnHeader':{
                        backgroundColor: '#f8f8f8',
                        '& .MuiDataGrid-columnHeaderTitle':{
                            color: '#444444',
                            fontWeight: '300',
                        }
                    },
                    '& .MuiDataGrid-pinnedColumns':{
                        backgroundColor:'#ffffff',
                        '& .MuiDataGrid-cell':{
                            backgroundColor: '#ffffff',
                            '& .MuiDataGrid-actionsCell':{
                                zIndex: 12,
                            }
                        }
                    },
                }
            }
        },
        MuiDialog:{
            styleOverrides: {
                root: {
                    '& .MuiDialog-paper':{
                        borderRadius: '4px',
                        boxShadow: '0 0 10px 0 rgb(0 0 0 / 10%)',
                    },
                    '& .MuiDialogContent-root':{
                        padding: '1rem',
                        paddingTop: '1rem!important',
                    },
                    '& .MuiDialogTitle-root':{
                        background: '#f8f8f8',
                        borderBottom: '1px solid #f2f2f2',
                    },
                    '& .MuiDialogActions-root':{
                        padding:'0 24px 24px'
                    }
                }
            }
        }
    },
}

const authDarkTheme = {
    ...baseTheme,
    palette: {
        ...baseTheme.palette,
    }
}

export const coreTheme = {
    components: {
        MuiPickersPopper:{
            styleOverrides: {
                root: {
                    zIndex: '9999!important',
                }
            }
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: '#273045',
                    color: '#ffffff',
                    '& .MuiToolbar-root': {
                        '& .MuiIconButton-root': {
                            color: '#ffffff',
                        }
                    }
                },
            },
        },
        MuiBreadcrumbs:{
            styleOverrides: {
                root: {
                    color: '#ffffff',
                },
            },
        },
        MuiFormHelperText:{
            styleOverrides: {
                root: {
                    color: '#ff0000',
                },
            },
        },
        MuiCard:{
            styleOverrides: {
                root: ({ theme }) => ({
                    color: '#333333',
                    border: `1px solid ${theme.palette.grey[300]}`,
                    boxShadow: theme?.card?.boxShadow || '0 0 2px 0 rgb(0 0 0 / 10%)',
                    borderRadius: theme?.card?.borderRadius || '8px',
                    "& .MuiCardHeader-root": {
                        padding: '1.5rem',
                        borderBottom: '1px solid #eff2f5',
                    },
                    '& .MuiCardContent-root':{
                        padding: '1.5rem',
                    },
                    '& .MuiCardContent-root:last-child':{
                        paddingBottom: '1.5rem',
                    },
                    "& .MuiCardActions-root":{
                        padding: '1rem',
                        borderTop: '1px solid #eff2f5',
                        background: 'transparent',
                        '& a': {
                            textDecoration: 'none'
                        }
                    }
                }),
            },
        },
        MuiButton:{
            variants: [
                {
                    props: { variant: 'accountButton' },
                    style: ({ theme }) => ({
                        border:`1px solid ${theme.palette.secondary.dark}`,
                        textTransform: 'none',
                        color: theme.palette.primary.contrastText,
                        '&:hover': {
                            borderColor: theme.palette.secondary.light,
                            color: theme.palette.secondary.contrastText,
                            '& .MuiTypography-root':{
                                color: theme.palette.secondary.contrastText,
                            }
                        },
                        '& .MuiTypography-root':{
                            color: theme.palette.secondary.contrastText,
                        }
                    }),
                },
            ],
            styleOverrides: {
                root: ({ theme, ownerState }) => {
                    // Check if the variant is 'text' (which is standard in MUI by default)
                    if (ownerState.variant === 'standard') {
                        const colorVariant = ownerState.color;
                        return {
                            textTransform: 'none',
                            backgroundColor: theme.palette[colorVariant]?.light, // Use light color from the theme
                            '&:hover': {
                                backgroundColor: theme.palette[colorVariant]?.dark || theme.palette.grey[500], // Use dark color for hover
                                color: theme.palette[colorVariant]?.contrastText || theme.palette.common.white, // Use contrast text color
                            },
                            '&.Mui-disabled': {
                                backgroundColor: theme.palette.action.disabled, // Disabled button color
                                color: theme.palette.action.disabledBackground, // Disabled text color
                            },
                        };
                    }

                    // Return empty object for other variants to keep default behavior
                    return {};
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: ({ theme, ownerState }) => {
                    if (ownerState.variant === 'filled') {
                        return {
                            fontSize: '0.70rem',
                            borderRadius: '4px',
                            backgroundColor: theme.palette[ownerState.color]?.main || theme.palette.grey[500],
                            color: theme.palette[ownerState.color]?.contrastText || theme.palette.common.white,
                            '&:hover': {
                                backgroundColor: theme.palette[ownerState.color]?.dark || theme.palette.grey[700],
                            },
                        };
                    } else if (ownerState.variant === 'outlined') {
                        return {
                            fontSize: '0.70rem',
                            borderRadius: '4px',
                            border: `1px solid ${theme.palette[ownerState.color]?.main || theme.palette.grey[500]}`,
                            color: theme.palette[ownerState.color]?.main || theme.palette.grey[500],
                            backgroundColor: theme.palette[ownerState.color]?.light || theme.palette.grey[300],
                            '&:hover': {
                                backgroundColor: theme.palette[ownerState.color]?.light || theme.palette.grey[300],
                                borderColor: theme.palette[ownerState.color]?.dark || theme.palette.grey[700],
                            },
                        };
                    }
                },
            }
        },
        MuiTextField: {
            styleOverrides: {
                root: ({ theme }) => ({
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: '#f9f9f9',
                        '& fieldset': {
                            border: '1px solid #ddd',
                        },
                        '&:hover fieldset': {
                            border: `2px solid ${theme.palette.primary.main}`,
                        },
                        '&:hover': {
                            backgroundColor: '#f8f8f8',
                        },
                        '&.Mui-focused': {
                            backgroundColor: '#f6f8ff',
                        },
                        '&.Mui-focused fieldset': {
                            border: `2px solid ${theme.palette.primary.main}`,
                        },
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                    },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: `2px solid ${theme.palette.primary.main}`,
                    },
                }),
            },
        },
        MuiSelect: {
            styleOverrides: {
                root: {
                    '& .MuiSelect-select': {
                        backgroundColor: '#f9f9f9',
                        borderRadius: '8px',
                    },
                    '& .MuiOutlinedInput-root': {
                        backgroundColor: '#f9f9f9',
                        borderRadius: '8px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                        border: '1px solid #ddd',
                    },
                    '&:hover .MuiOutlinedInput-root': {
                        backgroundColor: '#eaeaea',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        border: '2px solid #a1a1a1',
                    },
                    '& .MuiOutlinedInput-root.Mui-focused': {
                        border: '2px solid #6200ea',
                        backgroundColor: '#e0e0e0',
                    },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: '2px solid #6200ea',
                    },
                    '& .MuiInputBase-input': {
                        color: '#333',
                    },
                },
            },
        },
        MuiTablePagination:{
            styleOverrides: {
                root: {
                    '& p': {
                        marginBottom: '0!important',
                    }
                }
            }
        },
        MuiDrawer:{
            styleOverrides: {
                root: {
                    '& .MuiDrawer-paper':{
                        position:'relative'
                    },
                    '& .MuiCardContent-root':{
                        padding:'1rem',
                    }
                }
            }
        },
        MuiPopover:{
            styleOverrides: {
                root: {
                    zIndex: '9999!important',
                }
            }
        },
        MuiLink:{
            styleOverrides: {
                root: {
                    cursor: 'pointer',
                    textDecoration: 'none',
                    color: '#00517c',
                    '&:hover': {
                        textDecoration: 'underline',
                        color: '#00324d',
                    },
                }
            }
        },
        MuiMenu:{
            styleOverrides: {
                root:{
                    '& .MuiPaper-root':{
                        borderRadius: '4px',
                        boxShadow: '0 0 10px 0 rgb(0 0 0 / 10%)',
                    },
                    '& .MuiList-root':{
                        padding: '0',
                        '& a':{
                            color: '#000',
                            '& li':{
                                padding: '10px 20px',
                                '&:hover':{
                                    backgroundColor: '#f5f5f5',
                                }
                            }
                        },
                        '& svg':{
                            marginRight: '10px',
                            fontSize:'1rem'
                        },
                    },
                }
            }
        },
        MuiMenuItem: {
            styleOverrides: {
                root: ({ theme }) => ({
                    '&.Mui-selected': {
                        backgroundColor: theme.palette.success.light,
                    },
                    '&.Mui-selected:hover': {
                        backgroundColor: theme.palette.success.light,
                    },
                }),
            },
        },
        MuiDataGrid: {
            styleOverrides: {
                cell: {
                    display: 'flex',
                    alignItems: 'center',
                },
                root: {
                    border: 'none',
                    '& .MuiDataGrid-iconSeparator': {
                        display: 'none',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                        borderBottom: 'none',
                        '& .MuiDataGrid-row--borderBottom': {
                            background: 'transparent',
                        }
                    },
                    '& .MuiDataGrid-columnHeader': {
                        outline: 'none',
                        border: 'none',
                        '&:focus-within': {
                            outline: 'none',
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            color: '#888888',
                            fontSize: '0.70rem',
                            textTransform: 'uppercase',
                            fontWeight: '600',
                        },
                    },
                    '& .MuiDataGrid-pinnedColumns': {
                        '& .MuiDataGrid-cell': {
                            '& .MuiDataGrid-actionsCell': {
                                zIndex: 12,
                            },
                        },
                    },
                    '& .MuiDataGrid-row': {
                        borderBottom: '1px dashed #eeeeee',
                    },
                    '& .MuiDataGrid-cell': {
                        border: 'none',
                    },
                    '& .MuiDataGrid-footerContainer': {
                        border: 'none',
                    },
                },
            },
        },
        MuiTable: {
            styleOverrides: {
                root: ({ theme }) => ({
                    border: 'none',
                    '& .MuiTableHead-root': {
                        '& .MuiTableRow-root': {
                            borderBottom: 'none',
                            '& .MuiTableCell-root': {
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                fontSize: '0.75rem',
                                color: '#aaaaaa',
                                padding: '16px',
                                borderBottom: 'none',
                            },
                        },
                    },
                    '& .MuiTableRow-root': {
                        '& .MuiTableCell-root': {
                            borderBottom: '1px dashed #eeeeee',
                            padding: '16px',
                            fontSize: '1rem',
                            color: '#666666',
                        },
                    },
                    '& .MuiTableCell-root': {
                        padding: '12px',
                        fontSize: '1rem',
                        color: '#555555',
                    },
                    '& .MuiTableBody-root': {
                        '& .MuiTableRow-root': {
                            transition: 'background-color 0.3s',
                            '&:last-of-type .MuiTableCell-root': {
                                borderBottom: 'none',
                            },
                        },
                    },
                }),
            },
        },
        MuiDialog:{
            styleOverrides: {
                root: ({ theme }) => ({
                    '& .MuiDialog-paper':{
                        borderRadius: '4px',
                        boxShadow: '0 0 10px 0 rgb(0 0 0 / 10%)',
                    },
                    '& .MuiDialogContent-root':{
                        padding: '2rem',
                        '& .MuiTableContainer-root': {
                        marginTop: theme.spacing(2),
                        '& .MuiTable-root': {
                            '& .MuiTableHead-root': {
                                '& .MuiTableRow-root': {
                                    '& .MuiTableCell-root': {
                                        fontWeight: 600,
                                        fontSize: '0.7rem',
                                    },
                                },
                            },
                            '& .MuiTableBody-root': {
                                '& .MuiTableRow-root': {
                                    '& .MuiTableCell-root': {
                                        fontSize: '0.8rem',
                                        '& .MuiAvatar-root': {
                                        },
                                        '& .employee_name': {
                                            fontSize: '0.8rem',
                                            fontWeight: 600,
                                        },
                                        '& .employee_code': {
                                            fontSize: '0.8rem',
                                            fontWeight: 400,
                                        },
                                    },
                                },
                            },
                            '& .MuiTableFooter-root': {
                            },
                        },
                    },
                    },
                    '& .MuiDialogTitle-root':{
                        background: '#f8f8f8',
                        borderBottom: '1px solid #f2f2f2',
                    },
                    '& .MuiDialogActions-root':{
                        paddingTop: '1rem',
                        padding:'0 2rem 2rem',
                        margin:0
                    }
                }),
            }
        },
    },
}

export const theme = {
    authLightTheme: createTheme(authLightTheme),
    authDarkTheme: createTheme(authDarkTheme),
    coreTheme: createTheme(coreTheme),
};
